"use client";

// ─────────────────────────────────────────────────────────────────────────────
// CommunityBuddyTour — community leg of the cross-page onboarding chain.
//
// Activates on `/community?onboarding_community=1` (set by the home
// tour's "community" stage when the user clicks Browse more on the
// Community Use Cases pill).
//
// Four beats (no scroll, no border throughout):
//   1. Intro spotlight on the community feed grid — explains what
//      these shared sessions are.
//   2. Highlight the filter bar — frames it as the way to narrow by
//      strategy type.
//   3. Click_target on the Options Trading filter pill — flips the
//      grid to options-only sessions.
//   4. Click_target on the first session card — the natural Link
//      navigates to /chat/{id}?onboarding_chat_share=1, where
//      CommunityChatBuddyTour picks up the next leg.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { BuddyOverlay, type BuddyBeat } from "./BuddyOverlay";
import { useClientBuddyScript } from "./useClientBuddyScript";
import {
  COMMUNITY_TOUR_QUERY_PARAM,
  isCommunityTourVisit,
} from "@/lib/onboarding-constants";

export function CommunityBuddyTour() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const shouldRun = isCommunityTourVisit(searchParams);

  const beats = useMemo<BuddyBeat[]>(
    () => [
      {
        beatId: "community_grid_intro",
        trigger: { type: "client" },
        copy: {
          title: "Real chats, shared by real traders",
          body:
            "Every card here is a research session a trader actually ran on Xynth, shared so you can fork it and run the same playbook on your own picks.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='community-feed']" },
        highlightOnly: true,
        hideSpotlightRing: true,
        noScroll: true,
        bubblePlacement: "topCenter",
        bubbleOffset: { x: 260 },
      },
      {
        beatId: "community_filter_bar_intro",
        trigger: { type: "client" },
        copy: {
          title: "Narrow by strategy",
          body:
            "Filter the sessions by the kind of trader you are. Options, stocks, technicals, fundamentals. Each pill rewrites the grid with sessions that fit.",
          cta: "Next",
        },
        anchor: { kind: "css", value: "[data-tour='community-filter-bar']" },
        highlightOnly: true,
        noScroll: true,
        bubblePlacement: "anchorBelow",
      },
      {
        beatId: "community_click_options_filter",
        trigger: { type: "client" },
        copy: {
          title: "Tap Options Trading",
          body:
            "Click the Options Trading filter to pull up sessions where traders dissected options flow, IV setups, and structured spreads on Xynth.",
        },
        anchor: { kind: "css", value: "[data-tour='community-filter-options']" },
        action: { kind: "click_target" },
        highlightOnly: true,
        noScroll: true,
        bubblePlacement: "anchorBelow",
      },
      {
        beatId: "community_open_first_card",
        trigger: { type: "client" },
        copy: {
          title: "Open one",
          body:
            "Click this session to step into the full chat. You'll see exactly what was asked, what Xynth pulled, and the conclusion the trader walked away with.",
        },
        anchor: { kind: "css", value: "[data-tour='community-first-card']" },
        action: { kind: "click_target" },
        highlightOnly: true,
        noScroll: true,
        bubblePlacement: "anchor",
      },
    ],
    [],
  );

  const stripQueryParam = useMemo(
    () => () => {
      try {
        const next = new URLSearchParams(searchParams?.toString() || "");
        next.delete(COMMUNITY_TOUR_QUERY_PARAM);
        const search = next.toString();
        const path = window.location.pathname + (search ? `?${search}` : "");
        router.replace(path, { scroll: false });
      } catch {
        /* URL mutation is best-effort. */
      }
    },
    [router, searchParams],
  );

  const { beat, onContinue, onExit, done } = useClientBuddyScript(
    shouldRun ? beats : [],
    {
      onComplete: () => {
        // No-op: the click_target on the first card already navigates
        // to the shared-chat page (with ?onboarding_chat_share=1) via
        // the natural Link click. CommunityChatBuddyTour takes over.
      },
      onExit: () => {
        stripQueryParam();
      },
    },
  );

  // After the user clicks the Options Trading filter, jump the page
  // back to the top so the first session card under the new filter is
  // immediately in view when the click_target beat advances.
  useEffect(() => {
    if (!shouldRun) return;
    if (beat?.beatId !== "community_open_first_card") return;
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [shouldRun, beat?.beatId]);

  if (!shouldRun || done) return null;

  return <BuddyOverlay beat={beat} onContinue={onContinue} onExit={onExit} />;
}
